import React, { useState } from 'react';
import { FiSearch, FiX, FiPlus, FiUsers } from 'react-icons/fi';
import { FaBuilding } from 'react-icons/fa';
import CRMSidebar from '../components/CRMSidebar';
import GenericAddModal from '../components/GenericAddModal';
import useLocalState from '../hooks/useLocalState';
import { sortData } from '../utils/tableUtils';

const initialData = [
  { ref: 'CU2602-0010', name: 'WONG CHING YEE', alias: 'Wong', type: 'Customer', city: 'Hong Kong', country: 'HK', status: 'Active' },
  { ref: 'SU2512-001', name: 'WELL MAY INTERNATIONAL GROUP', alias: 'Well May', type: 'Supplier', city: 'Guangzhou', country: 'CN', status: 'Active' },
  { ref: 'CU2601-002', name: 'ALISON TAM', alias: 'Alison', type: 'Customer', city: 'Hong Kong', country: 'HK', status: 'Active' },
  { ref: 'CU2601-003', name: 'SHEREE', alias: 'Sheree', type: 'Customer', city: 'London', country: 'UK', status: 'Active' },
];

export default function ThirdParties() {
  const [data, setData] = useLocalState('third_parties_data_v3', initialData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'ref', direction: 'desc' });

  const handleAdd = (newData) => {
    setData([{ ...newData, status: 'Active' }, ...data]);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = sortData(data, sortConfig);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 ml-1">▲</span>;
    return <span className="text-[10px] text-black ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="flex h-full font-sans overflow-hidden bg-white">
      <CRMSidebar />
      <div className="flex-1 flex flex-col bg-[#a6e4e7] p-2">
        <div className="flex justify-between items-center mb-3 px-1 text-gray-800">
          <div className="flex items-center space-x-2">
            <FiUsers className="text-xl opacity-70 text-teal-800" />
            <span className="font-medium text-[15px] text-teal-700">Third-parties ({data.length})</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <select className="border border-gray-400 bg-white py-1 px-2 text-sm rounded-sm outline-none w-16 shadow-sm">
              <option>20</option>
            </select>
            <button onClick={() => setIsModalOpen(true)} className="bg-[#0000a0] text-white p-1.5 rounded shadow-sm hover:bg-blue-900 transition-colors">
              <FiPlus className="text-sm" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto bg-white border border-black shadow-sm">
          <table className="min-w-full border-collapse text-[12px] text-gray-800">
            <thead>
              <tr className="bg-[#a6e4e7] text-teal-900 select-none">
                <th onClick={() => handleSort('ref')} className="font-normal p-2 border border-black text-left cursor-pointer hover:bg-teal-200 group">Ref. <SortIcon column="ref" /></th>
                <th onClick={() => handleSort('name')} className="font-normal p-2 border border-black text-left cursor-pointer hover:bg-teal-200 group">Third-party Name <SortIcon column="name" /></th>
                <th onClick={() => handleSort('alias')} className="font-normal p-2 border border-black text-left cursor-pointer hover:bg-teal-200 group">Alias <SortIcon column="alias" /></th>
                <th onClick={() => handleSort('type')} className="font-normal p-2 border border-black text-left cursor-pointer hover:bg-teal-200 group">Type <SortIcon column="type" /></th>
                <th onClick={() => handleSort('city')} className="font-normal p-2 border border-black text-left cursor-pointer hover:bg-teal-200 group">City <SortIcon column="city" /></th>
                <th onClick={() => handleSort('country')} className="font-normal p-2 border border-black text-center cursor-pointer hover:bg-teal-200 group">Country <SortIcon column="country" /></th>
                <th className="font-normal p-2 border border-black text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <tr key={idx} className="bg-white hover:bg-blue-50 transition-colors">
                  <td className="p-2 border border-black font-medium">{row.ref}</td>
                  <td className="p-2 border border-black text-[#0000ee] hover:underline cursor-pointer">
                    <FaBuilding className="inline mr-1.5 text-gray-500" /> {row.name}
                  </td>
                  <td className="p-2 border border-black text-gray-600">{row.alias}</td>
                  <td className="p-2 border border-black">
                    <span className={`px-2 py-0.5 rounded text-[10px] ${row.type === 'Customer' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                      {row.type}
                    </span>
                  </td>
                  <td className="p-2 border border-black text-gray-800">{row.city}</td>
                  <td className="p-2 border border-black text-center text-gray-800">{row.country}</td>
                  <td className="p-2 border border-black text-center">
                    <span className="bg-[#e6f8e6] text-[#008000] border border-[#b7eb8f] px-2 py-0.5 rounded-[3px] text-[11px]">
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <GenericAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={handleAdd} title="Add Third-party" fields={[
          { name: 'ref', label: 'Reference (e.g. CU2602-005)' }, { name: 'name', label: 'Company / Name' }, { name: 'alias', label: 'Alias / Short Name' },
          { name: 'type', label: 'Type (Customer / Supplier)' }, { name: 'city', label: 'City' }, { name: 'country', label: 'Country Code (e.g. HK)' }
        ]} />
      </div>
    </div>
  );
}